import classNames from 'classnames/bind';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import React, { useEffect } from 'react';

import {
  IMAGE_SHOW_DURATION,
  LOGO_SHOW_DURATION,
  ANIMATION_SPEED_FACTOR,
  HIDE_DURATION,
  SHOW_DELAY,
  IMAGE_DELAY,
  LETTERS_COLOR_DELAY,
  NIKE_LOGO_DURATION,
  SHOW_EASE,
  IMAGE_SHOW_EASE,
  HIDE_EASE,
} from 'constants/hero-animation-variables';
import NikeLogo from 'images/pages/home/hero/nike.inline.svg';

import imageStyles from '../image-styles.module.scss';
import Trademark from '../trademark';

import styles from './slide-nike.module.scss';

const cx = classNames.bind(styles);
const imgCx = classNames.bind(imageStyles);

const SlideNike = () => {
  const {
    nikeRight,
    nikeLeft,
    nikeTop,
    nikeBottom,
    // eslint-disable-next-line no-use-before-define
  } = useStaticQuery(IMAGE_QUERY);

  const trademarkVariants = {
    initial: {
      opacity: 0,
    },
    appear: {
      opacity: 0,
    },
    showNikeInitial: {
      opacity: 1,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * LETTERS_COLOR_DELAY * 4,
        duration: 0,
      },
    },
    hideNike: {
      y: '-100vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    },
    showNike: {
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SHOW_DELAY,
        duration: ANIMATION_SPEED_FACTOR * LOGO_SHOW_DURATION,
        ease: SHOW_EASE,
      },
    },
  };

  const nikeSmallVariants = {
    initial: {
      opacity: 0,
    },
    appear: {
      opacity: 0,
    },
    showNikeInitial: {
      opacity: 1,
      transition: { delay: ANIMATION_SPEED_FACTOR * (LETTERS_COLOR_DELAY * 2), duration: 0 },
    },
    showNike: {
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SHOW_DELAY,
        duration: ANIMATION_SPEED_FACTOR * LOGO_SHOW_DURATION,
      },
    },
    hideNike: {
      y: '-100vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    },
  };

  const imageVariants = {
    initial: {
      y: '100vh',
    },
    appear: {
      y: '100vh',
    },
    showNikeInitial: (custom) => ({
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * (IMAGE_DELAY[custom].show + SHOW_DELAY),
        duration: ANIMATION_SPEED_FACTOR * IMAGE_SHOW_DURATION,
        ease: IMAGE_SHOW_EASE,
      },
    }),
    showNike: (custom) => ({
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * (IMAGE_DELAY[custom].show + SHOW_DELAY),
        duration: ANIMATION_SPEED_FACTOR * IMAGE_SHOW_DURATION,
        ease: IMAGE_SHOW_EASE,
      },
    }),
    hideNike: (custom) => ({
      y: '-100vh',
      transition: {
        delay: ANIMATION_SPEED_FACTOR * IMAGE_DELAY[custom].hide,
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
      },
    }),
  };

  const nikeLogoPathVarians = {
    initial: {
      pathLength: 0,
    },
    appear: {
      pathLength: 1,
      transition: {
        duration: ANIMATION_SPEED_FACTOR * NIKE_LOGO_DURATION,
        ease: [0, 0, 1, 1],
      },
    },
  };

  const nikeLogoVariants = {
    initial: {
      y: 0,
    },
    showNike: {
      y: 0,
      transition: {
        delay: ANIMATION_SPEED_FACTOR * SHOW_DELAY,
        duration: ANIMATION_SPEED_FACTOR * LOGO_SHOW_DURATION,
        ease: SHOW_EASE,
      },
    },
    hideNike: {
      y: '-120vh',
      transition: {
        duration: ANIMATION_SPEED_FACTOR * HIDE_DURATION,
        ease: HIDE_EASE,
      },
      transitionEnd: {
        y: '100vh',
        pathLength: 1,
      },
    },
  };

  return (
    <>
      {/* Nike logo large */}
      <motion.div className={cx('nike-logo')} variants={nikeLogoVariants}>
        <svg
          width="1070"
          height="383"
          viewBox="0 0 1070 383"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            d="M24.1728 339.556L23.3887 340.176L24.1727 339.555C15.9658 329.191 10.4031 319.128 6.6936 307.972C-4.73612 273.588 1.10788 228.307 23.6449 178.425C42.927 135.753 72.6976 93.3857 124.739 34.6188C128.571 30.2963 136.196 21.8106 142.883 14.4035C145.16 11.8813 147.328 9.48474 149.2 7.42094C136.41 29.5685 125.543 55.1051 119.493 77.37C109.316 114.783 110.502 147.038 123.146 172.131L123.147 172.131C131.861 189.403 146.794 204.362 163.614 212.642L163.614 212.642C193.009 227.107 235.875 228.24 287.98 216.184L287.981 216.184C291.584 215.345 469.07 168.025 682.362 111.036C789.018 82.5354 885.985 56.6356 956.292 37.8655C991.445 28.4805 1019.93 20.878 1039.63 15.6241C1046.62 13.7604 1052.51 12.1922 1057.19 10.9449C1054.48 12.1108 1051.35 13.4573 1047.82 14.9746C1033.78 21.0197 1013.41 29.7761 988.175 40.6278C937.697 62.3312 867.706 92.4156 789.674 125.952C633.608 193.024 445.373 273.904 316.725 329.158C275.98 346.653 265.11 351.061 245.987 357.804L246.32 358.747L245.987 357.804C197.087 375.051 153.355 383.253 118.069 381.845C86.3519 380.58 60.4866 371.869 40.2917 355.756C36.4587 352.694 27.2376 343.428 24.1728 339.556Z"
            stroke="#1A1A1A"
            strokeWidth="2"
            variants={nikeLogoPathVarians}
          />
        </svg>
      </motion.div>

      {/* SLIDE 1 images */}
      <motion.div variants={imageVariants} className={imgCx('img-left')} custom="left">
        <GatsbyImage fluid={nikeLeft.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-bottom')} custom="bottom">
        <GatsbyImage fluid={nikeBottom.childImageSharp.fluid} fadeIn={false} />
      </motion.div>

      <motion.div variants={imageVariants} className={imgCx('img-top')} custom="top">
        <GatsbyImage fluid={nikeTop.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div variants={imageVariants} className={imgCx('img-right')} custom="right">
        <GatsbyImage fluid={nikeRight.childImageSharp.fluid} fadeIn={false} />
      </motion.div>
      <motion.div className={cx('trademark')} variants={trademarkVariants}>
        <Trademark />
      </motion.div>
      <motion.div className={cx('nike-small')} variants={nikeSmallVariants}>
        <NikeLogo />
      </motion.div>
    </>
  );
};

export default SlideNike;

const IMAGE_QUERY = graphql`
  query nikeImages {
    nikeRight: file(relativePath: { eq: "pages/home/hero/nike-right.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    nikeLeft: file(relativePath: { eq: "pages/home/hero/nike-left.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    nikeTop: file(relativePath: { eq: "pages/home/hero/nike-top.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    nikeBottom: file(relativePath: { eq: "pages/home/hero/nike-bottom.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`;
